import React from 'react';
import { Language, t } from '../data/translations';
import trumpetPng from '../assets/custom/scroll_set/trumpet_green.png';

interface GameModalProps {
    title: string;
    message: string;
    onRestart: () => void;
    onToMenu?: () => void;
    language: Language;
    variant?: 'default' | 'victory';
    restartLabel?: string;
}

const GameModal: React.FC<GameModalProps> = ({
    title,
    message,
    onRestart,
    onToMenu,
    language,
    variant = 'default',
    restartLabel
}) => {
    const isVictory = variant === 'victory';

    return (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className={`relative flex flex-col items-center max-w-md w-full mx-4 transition-all duration-700 animate-in fade-in zoom-in slide-in-from-bottom-8`}>
                
                {/* Levitating Trumpets for Victory */}
                {isVictory && (
                    <div className="absolute -top-32 w-full flex justify-center items-center pointer-events-none animate-bounce-slow">
                        <div className="relative w-48 h-32 flex justify-center items-center">
                            <img
                                src={trumpetPng}
                                className="absolute w-full h-full object-contain transform rotate-[30deg] origin-bottom-right drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
                                alt="Trumpet Left"
                                style={{ right: 'calc(42% - 30px)' }}
                            />
                            <img
                                src={trumpetPng}
                                className="absolute w-full h-full object-contain transform -scale-x-100 -rotate-[30deg] origin-bottom-right drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
                                alt="Trumpet Right"
                                style={{ left: 'calc(42% - 30px)' }}
                            />
                        </div>
                    </div>
                )}

                {/* Modal Container */}
                <div className={`
                    ${isVictory 
                        ? 'bg-[#2d1b0d] border-[#8b5e34] shadow-[0_0_50px_rgba(0,0,0,0.8),inset_0_0_20px_rgba(139,94,52,0.3)]' 
                        : 'bg-slate-800 border-slate-600 shadow-2xl'
                    } 
                    rounded-xl p-10 text-center text-white border-2 flex flex-col gap-6 items-center w-full relative overflow-hidden
                `}>
                    
                    {/* Victory Theme Background Elements */}
                    {isVictory && (
                        <div className="absolute inset-0 pointer-events-none opacity-10">
                            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,#8b5e34_0%,transparent_70%)]" />
                        </div>
                    )}

                    <h2 className={`
                        ${isVictory ? 'text-4xl font-serif text-amber-200 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]' : 'text-3xl font-bold'}
                        mb-2 uppercase tracking-tighter
                    `}>
                        {title}
                    </h2>
                    
                    <p className={`
                        ${isVictory ? 'text-xl font-serif text-amber-100/90 leading-relaxed' : 'text-lg'}
                        mb-4
                    `}>
                        {message}
                    </p>

                    <div className="flex flex-row gap-4 w-full justify-center mt-2">
                        {onToMenu && (
                            <button 
                                onClick={onToMenu} 
                                className={`
                                    ${isVictory 
                                        ? 'bg-[#4a3421] hover:bg-[#5d412a] border-amber-900/50' 
                                        : 'bg-slate-600 hover:bg-slate-500 border-slate-500'
                                    } 
                                    text-white font-bold py-3 px-8 rounded-lg border-b-4 transition-all duration-200 active:translate-y-1 active:border-b-0
                                `}
                            >
                                {t('ui.toMenu', language)}
                            </button>
                        )}
                        <button 
                            onClick={() => onRestart()} 
                            className={`
                                ${isVictory 
                                    ? 'bg-gradient-to-b from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 border-amber-900' 
                                    : 'bg-indigo-600 hover:bg-indigo-500 border-indigo-800'
                                } 
                                text-white font-bold py-3 px-8 rounded-lg border-b-4 transition-all duration-200 active:translate-y-1 active:border-b-0
                            `}
                        >
                            {restartLabel || (isVictory ? t('ui.continue', language) : t('ui.playAgain', language))}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0) rotate(-2deg); }
                    50% { transform: translateY(-20px) rotate(2deg); }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 4s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

export default GameModal;

